import React, { useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import toast from 'react-hot-toast';
import { getEkycUsage, EkycUsageRow } from '../../../../api/admin/ekyc.api';
import { hasAnyPermission, PERMISSION } from '../../../../utils/admin-permissions';
import { AdminCard, AdminCardFooter, AdminPageHeader, AdminPillTabs } from '../../ui';
import { formatMonth, monthKeysVn, summarizeEkycUsage } from './ekyc-usage';

const RANGES = [3, 6, 12] as const;
type Range = (typeof RANGES)[number];

const fmt = (n: number) => n.toLocaleString('vi-VN');

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

export const ManagementEkycUsageInner: React.FC = () => {
  const [months, setMonths] = useState<Range>(6);
  const [rows, setRows] = useState<EkycUsageRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getEkycUsage(months)
      .then((r) => alive && setRows(r))
      .catch(() => {
        if (!alive) return;
        setRows([]);
        toast.error('Không tải được báo cáo lượt eKYC.');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [months]);

  const data = useMemo(() => summarizeEkycUsage(rows, monthKeysVn(new Date(), months)), [rows, months]);
  const total = useMemo(
    () =>
      data.reduce(
        (acc, x) => ({
          docOk: acc.docOk + x.docOk,
          docFail: acc.docFail + x.docFail,
          duplicate: acc.duplicate + x.duplicate,
          faceOk: acc.faceOk + x.faceOk,
          faceFail: acc.faceFail + x.faceFail,
          providerError: acc.providerError + x.providerError,
          paidTotal: acc.paidTotal + x.paidTotal,
        }),
        { docOk: 0, docFail: 0, duplicate: 0, faceOk: 0, faceFail: 0, providerError: 0, paidTotal: 0 }
      ),
    [data]
  );

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Lượt eKYC"
        description="Số lần Cab gửi giấy tờ và khuôn mặt sang nhà cung cấp eKYC theo từng tháng, tính theo giờ Việt Nam."
        actions={
          <AdminPillTabs<Range>
            ariaLabel="Khoảng thời gian"
            value={months}
            onChange={setMonths}
            items={RANGES.map((m) => ({ key: m, label: `${m} tháng` }))}
          />
        }
      />

      <AdminCard flush>
        <Spin spinning={loading}>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Tháng</th>
                  <th className="adm-num">Đọc giấy tờ thành công</th>
                  <th className="adm-num">Đọc giấy tờ hỏng</th>
                  <th className="adm-num">Trùng căn cước</th>
                  <th className="adm-num">Khuôn mặt thành công</th>
                  <th className="adm-num">Khuôn mặt hỏng</th>
                  <th className="adm-num">Lỗi nhà cung cấp</th>
                  <th className="adm-num">Tổng lượt gọi trả phí</th>
                </tr>
              </thead>
              <tbody>
                {data.map((x) => (
                  <tr key={x.month}>
                    <td className="whitespace-nowrap">{formatMonth(x.month)}</td>
                    <td className="adm-num">{fmt(x.docOk)}</td>
                    <td className="adm-num">{fmt(x.docFail)}</td>
                    <td className="adm-num">{fmt(x.duplicate)}</td>
                    <td className="adm-num">{fmt(x.faceOk)}</td>
                    <td className="adm-num">{fmt(x.faceFail)}</td>
                    <td className="adm-num">{fmt(x.providerError)}</td>
                    <td className="adm-num font-semibold">{fmt(x.paidTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--adm-bg)', fontWeight: 600, color: 'var(--adm-heading)' }}>
                  <td style={{ padding: '14px 20px' }}>Cộng {months} tháng</td>
                  <td className="adm-num">{fmt(total.docOk)}</td>
                  <td className="adm-num">{fmt(total.docFail)}</td>
                  <td className="adm-num">{fmt(total.duplicate)}</td>
                  <td className="adm-num">{fmt(total.faceOk)}</td>
                  <td className="adm-num">{fmt(total.faceFail)}</td>
                  <td className="adm-num">{fmt(total.providerError)}</td>
                  <td className="adm-num">{fmt(total.paidTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Spin>
        <AdminCardFooter>
          <p className="text-sm adm-muted" style={{ margin: 0 }}>
            Tổng lượt gọi trả phí gồm mọi lần đọc giấy tờ, kể cả lần thành công, lần hỏng, lần phát hiện trùng căn
            cước và lần nhà cung cấp báo lỗi, cộng với mọi lần kiểm tra khuôn mặt thành công, hỏng hoặc bị nhà cung
            cấp báo lỗi. Lần trùng căn cước ở bước khuôn mặt chỉ là Cab tự đối chiếu trong hệ thống nên không tính
            vào tổng này. Lỗi nhà cung cấp vẫn được tính để con số không bị thấp hơn hoá đơn thật.
          </p>
        </AdminCardFooter>
      </AdminCard>
    </div>
  );
};

const ManagementEkycUsage: React.FC = () =>
  hasAnyPermission(PERMISSION.UserManage) ? <ManagementEkycUsageInner /> : <NoAccess />;

export default ManagementEkycUsage;
